import React, { useEffect, useMemo, useState } from "react";
import { api, type AgentRegistryView, type ProjectWorkflowStartResult } from "../api";
import { ownerFacingGlobalError } from "../ownerFacingGlobalError";

type Route = NonNullable<AgentRegistryView["taskRouting"]>["routes"][number];
export type HitlDecision = "pending" | "approved" | "rejected" | "edited";

export interface GovernedExecutionEvidencePack {
  status: "awaiting_owner_gate" | "needs_workflow_start";
  summary: string;
  checks: Array<{ id: string; label: string; ok: boolean; evidence: string }>;
  blockedAutomation: string[];
  nextEvidence: string[];
}

export interface GovernedExecutionDecisionReadback {
  status: "packet_ready" | "needs_workflow_start";
  summary: string;
  nextGate: string;
  evidence: string;
  options: Array<{ id: string; label: string; ok: boolean; meaning: string }>;
  requiredEvidence: string[];
  blockedActions: string[];
}

function agentName(registry: AgentRegistryView | null, id: string): string {
  return registry?.agents.find((agent) => agent.id === id)?.name ?? id;
}

function compactList(items: string[], limit = 3): string {
  if (items.length === 0) return "-";
  const shown = items.slice(0, limit).join(", ");
  return items.length > limit ? `${shown} +${items.length - limit}` : shown;
}

function projectWorkflowErrorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function projectWorkflowErrorMessage(error: unknown): string {
  const text = projectWorkflowErrorText(error);
  if (/route.*not found|unknown.*route|invalid.*route|project_workflow_route/i.test(text)) {
    return "Projekt-Route ist nicht mehr aktuell. Bitte Route neu laden und Workflow erneut planen.";
  }
  if (/approval.*not found|already decided|not awaiting|invalid_state|approval_invalid|approval_expired/i.test(text)) {
    return "Projekt-Workflow-Gate ist nicht mehr aktuell. Bitte Workflow-Status neu laden und Owner-Gate erneut pruefen.";
  }
  return ownerFacingGlobalError(text);
}

function Step({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-lg border border-jarvis-border/55 bg-jarvis-bg/35 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-jarvis-muted">{label}</div>
      <div className="mt-1 min-w-0 truncate text-sm text-jarvis-text" title={value}>{value}</div>
    </div>
  );
}

export function buildGovernedExecutionEvidencePack(
  result: ProjectWorkflowStartResult | null,
  decision: HitlDecision,
): GovernedExecutionEvidencePack {
  if (!result) {
    return {
      status: "needs_workflow_start",
      summary: "Noch kein agentischer Projekt-Workflow gestartet; es gibt keine Audit-/Approval-Evidence.",
      checks: [
        { id: "workflow", label: "Workflow gestartet", ok: false, evidence: "kein Workflow" },
        { id: "approval", label: "Owner Approval angelegt", ok: false, evidence: "kein Approval" },
        { id: "execution", label: "Execution blockiert", ok: true, evidence: "UI zeigt keine Execute-Aktion" },
      ],
      blockedAutomation: ["kein Tool-Run", "kein Runner-Job", "kein Shell/Git/Deploy"],
      nextEvidence: ["Workflow mit Ziel und Route starten", "Approval-/Audit-Ref pruefen", "Owner-Entscheidung erfassen"],
    };
  }

  const executionBlocked =
    result.executionAllowed === false &&
    result.implementation.executionAllowed === false &&
    result.implementation.toolExecution === false &&
    result.implementation.runnerJobs === false &&
    result.implementation.shellGitDeploy === false &&
    result.implementation.autoExecute === false &&
    result.approval.executeOnApproval === false;

  const allTasksGated = result.plan.tasks.every((task) => task.executionAllowed === false && task.evidenceRequired === true);
  const allReviewsGated = result.reviews.every((review) => review.executionAllowed === false && review.requiredDecisionBy === "owner");

  return {
    status: "awaiting_owner_gate",
    summary: "Workflow ist als Run und Approval angelegt; Umsetzung bleibt bis zu einem spaeteren Execution-Gate blockiert.",
    checks: [
      { id: "workflow", label: "Workflow/Run angelegt", ok: Boolean(result.workflowId && result.runId), evidence: `${result.workflowId} / ${result.runId}` },
      { id: "audit", label: "Audit Evidence", ok: Boolean(result.evidence.evidenceRef), evidence: result.evidence.evidenceRef },
      { id: "approval", label: "Owner Approval", ok: result.approval.requiredDecisionBy === "owner" && result.approval.status === "pending", evidence: result.evidence.approvalRef },
      { id: "tasks", label: "Tasks evidence-gated", ok: allTasksGated, evidence: `${result.plan.tasks.length} Tasks` },
      { id: "reviews", label: "Reviews owner-gated", ok: allReviewsGated, evidence: `${result.reviews.length} Reviews · decision=${decision}` },
      { id: "execution", label: "Execution blockiert", ok: executionBlocked, evidence: result.implementation.status },
    ],
    blockedAutomation: ["kein Tool-Run", "kein Runner-Job", "kein Shell/Git/Deploy", "kein Auto-Execute", "kein executeOnApproval"],
    nextEvidence: [
      "Owner prueft Approval und Evidence Preview",
      "Akzeptanzkriterien/No-Go-Grenzen im HITL-Feld dokumentieren",
      "Spaeteres Execution-Gate separat beweisen, bevor Umsetzung erlaubt wird",
    ],
  };
}

export function buildGovernedExecutionDecisionReadback(
  result: ProjectWorkflowStartResult | null,
): GovernedExecutionDecisionReadback {
  if (!result) {
    return {
      status: "needs_workflow_start",
      summary: "Noch kein Owner Decision Packet vorhanden; starte zuerst einen Workflow.",
      nextGate: "workflow_start_required",
      evidence: "kein Audit/Approval",
      options: [
        { id: "A", label: "A erlauben", ok: false, meaning: "wartet auf Workflow" },
        { id: "B", label: "B ablehnen", ok: false, meaning: "wartet auf Workflow" },
        { id: "C", label: "C aendern", ok: false, meaning: "wartet auf Workflow" },
      ],
      requiredEvidence: ["Workflow starten", "Audit-/Approval-Refs laden", "Owner-Grenzen erfassen"],
      blockedActions: ["no_tool_execution", "no_runner_jobs", "no_shell_git_deploy"],
    };
  }

  const packet = result.ownerDecisionPacket;
  return {
    status: "packet_ready",
    summary: packet.ownerPrompt,
    nextGate: packet.nextGate,
    evidence: `${packet.evidenceRef} / ${packet.approvalRef} / ${packet.handoffId}`,
    options: packet.decisionOptions.map((option) => ({
      id: option.id,
      label: option.label,
      ok: option.statusOnly === true && option.executeOnApproval === false,
      meaning: option.meaning,
    })),
    requiredEvidence: packet.requiredOwnerEvidence,
    blockedActions: packet.blockedActions,
  };
}

function HumanLoopPanel({
  result,
  answer,
  decision,
  onAnswer,
  onDecision,
}: {
  result: ProjectWorkflowStartResult | null;
  answer: string;
  decision: HitlDecision;
  onAnswer: (value: string) => void;
  onDecision: (value: HitlDecision) => void;
}): JSX.Element {
  const question = result
    ? "Welche Akzeptanzkriterien, betroffenen Repositories und No-Go-Grenzen soll das Agent-Team vor der Umsetzung beachten?"
    : "Starte zuerst einen Workflow, dann sammelt SIMO hier Rueckfragen, Entscheidung und Evidence Preview.";
  const evidencePreview = result
    ? JSON.stringify({
      approvalId: result.approval.id,
      evidenceRef: result.evidence.evidenceRef,
      approvalRef: result.evidence.approvalRef,
      riskLevel: result.approval.riskLevel,
      executeOnApproval: result.approval.executeOnApproval,
      executionAllowed: result.executionAllowed,
      noSilentExecution: result.noSilentExecution,
      goal: result.goal,
    }, null, 2)
    : "Noch keine Evidence verfuegbar.";

  return (
    <section className="panel simo-accent-confirming">
      <header className="panel-head">
        <div className="flex min-w-0 items-center gap-2">
          <span className="status-dot" />
          <span className="panel-title truncate">Human-in-the-loop Review Desk</span>
        </div>
        <span className="kpi text-xs text-jarvis-muted">{decision}</span>
      </header>
      <div className="panel-body grid gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(280px,0.75fr)]">
        <div className="space-y-3">
          <div className="rounded-lg border border-jarvis-border/55 bg-jarvis-bg/35 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-jarvis-muted">Agent Rueckfrage</div>
            <p className="mt-1 text-sm leading-relaxed text-jarvis-text">{question}</p>
          </div>
          <label className="block">
            <span className="mb-1 block text-[10px] uppercase tracking-wider text-jarvis-muted">Antwort / Edit</span>
            <textarea
              className="input simo-keyboard-safe-field min-h-[112px] w-full resize-y leading-relaxed"
              value={answer}
              onChange={(event) => {
                onAnswer(event.target.value);
                onDecision("edited");
              }}
              placeholder="Antwort, Korrektur oder Entscheidungshinweis fuer das Agent-Team erfassen."
              maxLength={900}
            />
          </label>
          <div className="grid gap-2 sm:grid-cols-3">
            <button
              type="button"
              className="btn"
              disabled={!result}
              onClick={() => onDecision("approved")}
              title="Status-only Approve, keine Ausfuehrung"
            >
              Approve
            </button>
            <button
              type="button"
              className="btn"
              disabled={!result}
              onClick={() => onDecision("rejected")}
              title="Status-only Reject, keine Ausfuehrung"
            >
              Reject
            </button>
            <button
              type="button"
              className="btn"
              disabled={!result}
              onClick={() => onDecision("edited")}
              title="Antwort bearbeiten und Entscheidung offen halten"
            >
              Edit
            </button>
          </div>
        </div>

        <div className="space-y-3">
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-1">
            <Step label="Risiko" value={result ? `${result.approval.riskLevel} / owner decision` : "pending workflow"} />
            <Step label="Decision" value={decision === "edited" ? "edited / pending owner review" : decision} />
          </div>
          <div className="rounded-lg border border-jarvis-border/55 bg-jarvis-bg/35 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-jarvis-muted">Risikoerklaerung</div>
            <div className="mt-2 flex flex-wrap gap-1 text-[9px] text-jarvis-muted">
              <span className="badge bg-jarvis-bg/60">owner approval required</span>
              <span className="badge bg-jarvis-bg/60">evidence preview required</span>
              <span className="badge bg-jarvis-bg/60">execution blocked</span>
              <span className="badge bg-jarvis-bg/60">status-only decision</span>
            </div>
          </div>
          <div className="rounded-lg border border-jarvis-border/55 bg-jarvis-bg/35 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-jarvis-muted">Evidence Preview</div>
            <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded bg-jarvis-bg/45 p-2 text-[10px] text-jarvis-muted simo-thin-scroll">
              {evidencePreview}
            </pre>
          </div>
          <div className="rounded-lg border border-jarvis-border/55 bg-jarvis-bg/35 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-jarvis-muted">Next Actions</div>
            <ol className="mt-2 list-decimal space-y-1 pl-4 text-xs leading-relaxed text-jarvis-muted">
              <li>Rueckfrage beantworten oder Antwort editieren.</li>
              <li>Evidence Preview pruefen.</li>
              <li>Status-only Approve oder Reject setzen.</li>
              <li>Ergebnis erst nach spaeterem Execution-Gate uebernehmen.</li>
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ProjectWorkflowView(): JSX.Element {
  const [registry, setRegistry] = useState<AgentRegistryView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [routeId, setRouteId] = useState("");
  const [goal, setGoal] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ProjectWorkflowStartResult | null>(null);
  const [hitlAnswer, setHitlAnswer] = useState("");
  const [hitlDecision, setHitlDecision] = useState<HitlDecision>("pending");
  const governedEvidence = useMemo(
    () => buildGovernedExecutionEvidencePack(result, hitlDecision),
    [result, hitlDecision],
  );
  const decisionReadback = useMemo(
    () => buildGovernedExecutionDecisionReadback(result),
    [result],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.agentRegistry()
      .then((value) => {
        if (cancelled) return;
        setRegistry(value);
        setError(null);
        const first = value.taskRouting?.routes[0]?.id ?? "";
        setRouteId((current) => current || first);
      })
      .catch((err) => {
        if (!cancelled) setError(projectWorkflowErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const routes = registry?.taskRouting?.routes ?? [];
  const selectedRoute = useMemo<Route | null>(
    () => routes.find((route) => route.id === routeId) ?? routes[0] ?? null,
    [routes, routeId],
  );
  const canStart = goal.trim().length >= 8 && selectedRoute != null && !busy;

  const start = async (): Promise<void> => {
    if (!selectedRoute || !canStart) return;
    setBusy(true);
    setError(null);
    try {
      const response = await api.startProjectWorkflow({ routeId: selectedRoute.id, goal: goal.trim() });
      setResult(response);
      setHitlDecision("pending");
    } catch (err) {
      setError(projectWorkflowErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="h-full min-h-0 overflow-y-auto p-3 sm:p-4 md:p-6 simo-thin-scroll">
      <div className="mx-auto flex max-w-7xl flex-col gap-4">
        <header className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-wide text-jarvis-text">Projects</h1>
            <p className="mt-1 max-w-3xl text-xs leading-relaxed text-jarvis-muted">
              Ziel beschreiben, Agent-Team wählen, Workflow planen und als Approval-pflichtigen Run verfolgen.
            </p>
          </div>
          <div className="flex flex-wrap gap-1 text-[9px] text-jarvis-muted">
            <span className="badge bg-jarvis-bg/60">No silent execution</span>
            <span className="badge bg-jarvis-bg/60">Approval required</span>
            <span className="badge bg-jarvis-bg/60">Evidence required</span>
          </div>
        </header>

        {error && (
          <div className="rounded-lg border border-rose-500/45 bg-rose-950/30 px-3 py-2 text-sm text-rose-100">
            {error}
          </div>
        )}

        <section className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.75fr)]">
          <div className="panel simo-accent-confirming">
            <header className="panel-head">
              <div className="flex min-w-0 items-center gap-2">
                <span className="status-dot" />
                <span className="panel-title truncate">Project Intake</span>
              </div>
              <span className="kpi text-xs text-jarvis-muted">{loading ? "LOAD" : "GATED"}</span>
            </header>
            <div className="panel-body space-y-3">
              <label className="block">
                <span className="mb-1 block text-[10px] uppercase tracking-wider text-jarvis-muted">Ziel</span>
                <textarea
                  className="input simo-keyboard-safe-field min-h-[132px] w-full resize-y leading-relaxed"
                  value={goal}
                  onChange={(event) => setGoal(event.target.value)}
                  placeholder="Beschreibe das Projektziel, erwartete Repos/Connectoren, Akzeptanzkriterien und Risiken."
                  maxLength={1200}
                />
              </label>

              <label className="block">
                <span className="mb-1 block text-[10px] uppercase tracking-wider text-jarvis-muted">Agent-Team / Route</span>
                <select
                  className="input simo-keyboard-safe-field w-full"
                  value={selectedRoute?.id ?? ""}
                  onChange={(event) => setRouteId(event.target.value)}
                  disabled={routes.length === 0}
                >
                  {routes.map((route) => (
                    <option key={route.id} value={route.id}>{route.label}</option>
                  ))}
                </select>
              </label>

              {selectedRoute && (
                <div className="grid gap-2 md:grid-cols-2">
                  <Step label="Orchestrator" value={agentName(registry, selectedRoute.orchestratorAgentId)} />
                  <Step label="Lead" value={agentName(registry, selectedRoute.teamLeadAgentId)} />
                  <Step label="Spezialisten" value={selectedRoute.specialistAgentIds.map((id) => agentName(registry, id)).join(", ")} />
                  <Step label="Capabilities" value={compactList(selectedRoute.requiredCapabilities, 5)} />
                </div>
              )}

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-wrap gap-1 text-[9px] text-jarvis-muted">
                  <span className="badge bg-jarvis-bg/60">Route policy-gated</span>
                  <span className="badge bg-jarvis-bg/60">Owner approval</span>
                  <span className="badge bg-jarvis-bg/60">Execute off</span>
                </div>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={!canStart}
                  onClick={() => void start()}
                  title={canStart ? "Workflow planen" : "Ziel und Route erforderlich"}
                >
                  {busy ? "Plane..." : "Workflow planen"}
                </button>
              </div>
            </div>
          </div>

          <aside className="panel simo-accent-neutral">
            <header className="panel-head">
              <div className="flex min-w-0 items-center gap-2">
                <span className="status-dot" />
                <span className="panel-title truncate">Route Boundary</span>
              </div>
              <span className="kpi text-xs text-jarvis-muted">{routes.length}</span>
            </header>
            <div className="panel-body space-y-2">
              {selectedRoute ? (
                <>
                  <Step label="Policy" value={selectedRoute.policyGate} />
                  <Step label="Approval" value={selectedRoute.approvalRequired ? "required" : "not required"} />
                  <Step label="Execution" value={selectedRoute.executionAllowed ? "allowed" : "blocked"} />
                  <Step label="Route" value={selectedRoute.blocked ? "blocked" : "available"} />
                </>
              ) : (
                <div className="text-sm text-jarvis-muted">Keine Projekt-Route geladen.</div>
              )}
            </div>
          </aside>
        </section>

        <HumanLoopPanel
          result={result}
          answer={hitlAnswer}
          decision={hitlDecision}
          onAnswer={setHitlAnswer}
          onDecision={setHitlDecision}
        />

        <section className="panel simo-accent-confirming">
          <header className="panel-head">
            <div className="flex min-w-0 items-center gap-2">
              <span className="status-dot" />
              <span className="panel-title truncate">Owner Decision Packet</span>
            </div>
            <span className="kpi text-xs text-jarvis-muted">{decisionReadback.status}</span>
          </header>
          <div className="panel-body grid gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(260px,0.55fr)]">
            <div className="space-y-3">
              <p className="text-sm leading-relaxed text-jarvis-muted">{decisionReadback.summary}</p>
              <div className="grid gap-2 sm:grid-cols-3">
                {decisionReadback.options.map((option) => (
                  <div key={option.id} className={`rounded-lg border px-3 py-2 ${option.ok ? "border-emerald-400/35 bg-emerald-950/15" : "border-amber-400/40 bg-amber-950/20"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] uppercase tracking-wider text-jarvis-muted">{option.label}</span>
                      <span className={option.ok ? "text-[10px] font-semibold text-emerald-200" : "text-[10px] font-semibold text-amber-200"}>
                        {option.ok ? "status-only" : "offen"}
                      </span>
                    </div>
                    <div className="mt-1 text-xs leading-relaxed text-jarvis-text">{option.meaning}</div>
                  </div>
                ))}
              </div>
              <div className="rounded-lg border border-jarvis-border/55 bg-jarvis-bg/35 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-jarvis-muted">Owner Evidence</div>
                <div className="mt-2 flex flex-wrap gap-1 text-[9px] text-jarvis-muted">
                  {decisionReadback.requiredEvidence.map((item) => (
                    <span key={item} className="badge bg-jarvis-bg/60">{item}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <Step label="Evidence" value={decisionReadback.evidence} />
              <Step label="Next Gate" value={decisionReadback.nextGate} />
              <div className="rounded-lg border border-jarvis-border/55 bg-jarvis-bg/35 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-jarvis-muted">Blockierte Aktionen</div>
                <div className="mt-2 flex flex-wrap gap-1 text-[9px] text-amber-100">
                  {decisionReadback.blockedActions.map((item) => (
                    <span key={item} className="badge bg-amber-950/35">{item}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="panel simo-accent-confirming">
          <header className="panel-head">
            <div className="flex min-w-0 items-center gap-2">
              <span className="status-dot" />
              <span className="panel-title truncate">Governed Execution Evidence Pack</span>
            </div>
            <span className="kpi text-xs text-jarvis-muted">{governedEvidence.status}</span>
          </header>
          <div className="panel-body grid gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(260px,0.55fr)]">
            <div className="space-y-3">
              <p className="text-sm leading-relaxed text-jarvis-muted">{governedEvidence.summary}</p>
              <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {governedEvidence.checks.map((check) => (
                  <div key={check.id} className={`rounded-lg border px-3 py-2 ${check.ok ? "border-emerald-400/35 bg-emerald-950/15" : "border-amber-400/40 bg-amber-950/20"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] uppercase tracking-wider text-jarvis-muted">{check.label}</span>
                      <span className={check.ok ? "text-[10px] font-semibold text-emerald-200" : "text-[10px] font-semibold text-amber-200"}>
                        {check.ok ? "belegt" : "offen"}
                      </span>
                    </div>
                    <div className="mt-1 min-w-0 break-words text-xs text-jarvis-text">{check.evidence}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-3">
              <div className="rounded-lg border border-jarvis-border/55 bg-jarvis-bg/35 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-jarvis-muted">Blockierte Automation</div>
                <div className="mt-2 flex flex-wrap gap-1 text-[9px] text-amber-100">
                  {governedEvidence.blockedAutomation.map((item) => (
                    <span key={item} className="badge bg-amber-950/35">{item}</span>
                  ))}
                </div>
              </div>
              <div className="rounded-lg border border-jarvis-border/55 bg-jarvis-bg/35 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-jarvis-muted">Naechste Evidence</div>
                <ol className="mt-2 list-decimal space-y-1 pl-4 text-xs leading-relaxed text-jarvis-muted">
                  {governedEvidence.nextEvidence.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </section>

        <section className="panel simo-accent-ok">
          <header className="panel-head">
            <div className="flex min-w-0 items-center gap-2">
              <span className="status-dot" />
              <span className="panel-title truncate">Workflow Progress</span>
            </div>
            <span className="kpi text-xs text-jarvis-muted">{result?.plan.status ?? "IDLE"}</span>
          </header>
          <div className="panel-body">
            {!result ? (
              <div className="text-sm text-jarvis-muted">Noch kein Workflow gestartet.</div>
            ) : (
              <div className="space-y-3">
                <div className="grid gap-2 md:grid-cols-4">
                  <Step label="Workflow" value={result.workflowId} />
                  <Step label="Run" value={result.runId} />
                  <Step label="Approval" value={`${result.approval.status} / ${result.approval.riskLevel}`} />
                  <Step label="Evidence" value={result.evidence.evidenceRef} />
                </div>
                <div className="grid gap-2 md:grid-cols-5">
                  {result.plan.tasks.map((task) => (
                    <div key={task.id} className="rounded-lg border border-jarvis-border/50 bg-jarvis-bg/30 px-2 py-2">
                      <div className="text-[10px] uppercase tracking-wider text-jarvis-muted">{task.id}</div>
                      <div className="mt-1 text-sm text-jarvis-text">{task.status}</div>
                      <div className="mt-1 flex flex-wrap gap-1 text-[9px] text-jarvis-muted">
                        <span className="badge bg-jarvis-bg/60">{task.executionAllowed ? "execute" : "no execute"}</span>
                        <span className="badge bg-jarvis-bg/60">{task.evidenceRequired ? "evidence" : "no evidence"}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1 text-[9px] text-jarvis-muted">
                  <span className="badge bg-jarvis-bg/60">executeOnApproval=false</span>
                  <span className="badge bg-jarvis-bg/60">shell/git/deploy=false</span>
                  <span className="badge bg-jarvis-bg/60">autoExecute=false</span>
                  <span className="badge bg-jarvis-bg/60">noSilentExecution=true</span>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
